/**
 * DepartmentStatusPanel — persistent operational state.
 *
 * The six-row Marketing Department panel. Every row displays a state
 * badge, the department label + mission, and a short detail line.
 * These are NOT AI agents and NOT fake progress bars — they are the
 * operational state of each department relative to the campaign.
 *
 * Code-names (Atlas / Nova / …) are reserved backend-side but never
 * displayed in Sprint 4. That fence is deliberate.
 */
import { Card } from "@/shared/ui/card";
import { cn } from "@/shared/lib/utils";
import { CheckCircle2, Clock, Circle, Loader2, AlertCircle } from "lucide-react";
import type { DepartmentRow, DepartmentStateCode } from "../../types";

const STATE_ICON: Record<DepartmentStateCode, JSX.Element> = {
  complete: <CheckCircle2 className="h-5 w-5 text-emerald-500" aria-hidden />,
  active:   <Loader2 className="h-5 w-5 text-primary animate-spin" aria-hidden />,
  waiting:  <Clock className="h-5 w-5 text-amber-500" aria-hidden />,
  standby:  <Circle className="h-5 w-5 text-muted-foreground" aria-hidden />,
  blocked:  <AlertCircle className="h-5 w-5 text-rose-500" aria-hidden />,
};

const STATE_TEXT_COLOR: Record<DepartmentStateCode, string> = {
  complete: "text-emerald-600",
  active:   "text-primary",
  waiting:  "text-amber-600",
  standby:  "text-muted-foreground",
  blocked:  "text-rose-600",
};

export function DepartmentStatusPanel({
  rows,
}: {
  rows: DepartmentRow[];
}): JSX.Element {
  return (
    <section data-testid="room-department-status">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          Marketing Department
        </p>
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">
          Who&apos;s on the campaign
        </h2>
      </div>

      <Card className="p-4 sm:p-6">
        <ul className="divide-y divide-border">
          {rows.map((row) => (
            <li
              key={row.key}
              className="py-4 flex items-start gap-4"
              data-testid={`room-department-${row.key}`}
            >
              <div className="mt-0.5 flex-shrink-0">{STATE_ICON[row.state]}</div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="font-semibold">{row.label}</span>
                  <span
                    className={cn(
                      "text-xs font-medium uppercase tracking-wide",
                      STATE_TEXT_COLOR[row.state],
                    )}
                    data-testid={`room-department-state-${row.key}`}
                  >
                    {row.state_label}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-0.5">{row.mission}</p>
                <p className="text-sm mt-1">{row.detail}</p>
              </div>
            </li>
          ))}
        </ul>
      </Card>
    </section>
  );
}
